import {
    useConfluenceIntent,
    useLoggedOut,
    useLoggedInHasJiraAndConfluence,
    useLoggedInHasJiraAndNotConfluence,
    useJiraW2AndAfterOrMultipleJiras,
    useLoggedOutHasJira
} from "@/components/user-context";

import { useEnglishLocale } from "../sentry-intl";

import { Manifest } from ".";

const booleanTypeGuard = (value: unknown): value is boolean =>
    typeof value === "boolean";

export const experiments = {
    highlightJSMJPDTemplatesOnHomepage: {
        experimentName: "highlight-jsm-jpd-templates-on-homepage-experiment",
        parameterName: "isVariant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedInHasJiraAndConfluence
        },
        metadata: {
            description: "Highlight JSM and JPD templates on the homepage hero",
            expectedReleaseDate: "05/01/2025",
            expectedRuntime: "7 weeks",
            clientOnly: true,
            context: {
                urls: ["/"]
            }
        }
    },
    jfindEmbeddedSignupOnHomepage: {
        experimentName: "jfind-1343-embedded-signup-on-wac-homepage",
        parameterName: "cohort",
        defaultValue: "control" as
            | "control"
            | "jfind-1343-embedded-signup-on-wac-homepage",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is
                | "control"
                | "jfind-1343-embedded-signup-on-wac-homepage" => {
                if (typeof value !== "string") {
                    return false;
                }
                return [
                    "control",
                    "jfind-1343-embedded-signup-on-wac-homepage"
                ].includes(value);
            }
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description: "Embedded Signup Form on www.atlassian.com",
            expectedReleaseDate: "05/01/2025",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    jfindInfrastructureConfirmation: {
        experimentName: "jfind-1447-gups-experiment",
        parameterName: "jfind-1447-gups-experiment",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: useLoggedOutHasJira
        },
        metadata: {
            description:
                "Confirm infrastructure for logged-out personalization",
            expectedReleaseDate: "05/28/2025",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    cfind3254LandingPageSubAnchor: {
        experimentName: "cfind-3254-landing-page-sub-anchor",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test if personalizing the landing page off the sub anchor will increase d1to6",
            expectedReleaseDate: "04/14/2025",
            expectedRuntime: "6 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    cfind356SetupByJtbd: {
        experimentName: "cfind-3565-setup-by-jtbd",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Tests if better onboarding selection for generatable content will increase d1to6",
            expectedReleaseDate: "06/09/2025",
            expectedRuntime: "6 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwpAddRelayToConfluenceTemplatesLanding: {
        experimentName: "gwp-3063-add-relay-to-conf-templates-landing",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Page-level experiment that re-implements the confluence templates search experience in Relay",
            expectedReleaseDate: "11/21/2024",
            expectedRuntime: "5 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwpAddRelayToConfluenceTemplateCategories: {
        experimentName: "gwp-3063-add-relay-to-conf-template-categories",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Page-level experiment that re-implements the confluence templates search experience in Relay",
            expectedReleaseDate: "11/21/2024",
            expectedRuntime: "5 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jfind1506AfterD7GetOrganicUsersBackToProduct: {
        experimentName: "jfind-1506-after-d7-gubtp-organic",
        parameterName: "cohort",
        defaultValue: "control" as
            | "control"
            | "jfind-1506-after-d7-gubtp-organic",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "jfind-1506-after-d7-gubtp-organic" => {
                if (typeof value !== "string") {
                    return false;
                }
                return [
                    "control",
                    "jfind-1506-after-d7-gubtp-organic"
                ].includes(value);
            }
        },
        targeting: {
            cohort: useJiraW2AndAfterOrMultipleJiras
        },
        metadata: {
            description:
                "Display Get Users Back to Product screen for users who have one Jira after D7 or multiple Jiras",
            expectedReleaseDate: "05/30/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jfind1384OptimizeJiraPricing: {
        experimentName: "jfind_1384_optimize_jira_pricing",
        parameterName: "value",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description: "Optimizing the Jira Pricing Page",
            expectedReleaseDate: "05/01/2025",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/software/jira/pricing"]
            }
        }
    },
    templatesTabInHeader: {
        experimentName: "templates_tab_in_wac_header_iteration_2",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return !useLoggedOut();
            }
        },
        metadata: {
            description:
                "Surfacing the templates tab in the WAC JSM product tour, adding a hover state to JSM templates in WAC, and enabling direct opening of crossflow from the WAC JSM templates page",
            expectedReleaseDate: "12/13/2024",
            expectedRuntime: "13 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    pricingDirectBuyCheckoutAA: {
        experimentName: "pricing_direct_buy_checkout_aa",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "AA test for an option to buy a monthly subscription without a trial from the WAC pricing pages",
            expectedReleaseDate: "12/06/2024",
            expectedRuntime: "4 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    pricingDirectBuyCheckout: {
        experimentName: "pricing_direct_buy_checkout",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "AB test for an option to buy a monthly subscription without a trial from the WAC pricing pages",
            expectedReleaseDate: "01/10/2025",
            expectedRuntime: "4 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    streamlineRedirectToProductConfluence: {
        experimentName: "jfind-1245-confluence-streamline-redirect",
        parameterName: "value",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useConfluenceIntent // all editions
        },
        metadata: {
            description:
                "If we reduce redirects to product, the signup flow will be more reliable",
            expectedReleaseDate: "02/07/2025",
            expectedRuntime: "5 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardTenant: {
        experimentName: "jira_project_wizard_during_signup_tenant",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard while provisioning is taking place",
            expectedReleaseDate: "15/02/2024",
            expectedRuntime: "5 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardForRouteBiz: {
        experimentName: "route_biz_to_kanban_revision",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for kanban project created after selecting business template while provisioning is taking place",
            expectedReleaseDate: "28/05/2025",
            expectedRuntime: "11 weeks",
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardForScrum: {
        experimentName: "jira_project_wizard_for_scrum",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for scrum while provisioning is taking place",
            expectedReleaseDate: "12/03/2024",
            expectedRuntime: "4 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardDevKanban: {
        experimentName: "jira_software_kanban_wizard_m2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for dev kanban while provisioning is taking place",
            expectedReleaseDate: "21/03/2024",
            expectedRuntime: "7 weeks",
            context: {
                urls: []
            }
        }
    },
    signupAddPaymentAb: {
        experimentName: "signup_trialing_add_payment_ab",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            // we need to fire our exposure event manually because we can't determine if the user is eligible from here
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment to enable users with valid sites to go to payment at the sign-up screen",
            expectedReleaseDate: "02/27/25",
            expectedRuntime: "TBD",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jfindSignupButtonResize: {
        experimentName: "jfind-1185-signup-button-update",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment on making the signup button smaller for JFIND",
            expectedReleaseDate: "02/07/25",
            expectedRuntime: "21 days",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    cfind1192SignupButtonResize: {
        experimentName: "jfind-1192-signup-button-update-cfind",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment on making the signup button smaller for CFIND",
            expectedReleaseDate: "02/07/25",
            expectedRuntime: "TBD",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerJira: {
        experimentName: "gwp-5090-experiment-banner_jira",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerJSM: {
        experimentName: "gwp-5090-experiment-banner-jsm",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Service Management product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerConfluence: {
        experimentName: "gwp-5090-experiment-banner-confluence",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Service Management product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    cfindSetupOverProvisioningV3: {
        experimentName: "cfind-3024-setup-over-provisioning-v3",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: `To reduce the perceived provisioning time and the resulting friction, 
                let's perform provisioning in the background while the user proceeds through the setup flow. 
                This will involve moving our account setup screens from in-product to wac-web.`,
            expectedReleaseDate: "03/03/25",
            expectedRuntime: "42 days",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5272ThreeArmedH1Experiment: {
        experimentName: "gwp-5272-three-arm-experiment-h1",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "variant-a" | "variant-b",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "variant-a" | "variant-b" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variant-a", "variant-b"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing h1 change on JSM anchor page paid-lp-jsm-itsm",
            expectedReleaseDate: "04/16/25",
            expectedRuntime: "3 weeks",
            context: {
                urls: [
                    "/software/jira/service-management?cmsVariant=paid-lp-jsm-itsm"
                ]
            }
        }
    },
    cfind3210PaidSearchTemplateKeywords: {
        experimentName: "cfind_3210_land_paid_search_template_keywords",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test the impact of landing paid search users looking for a template on template details page",
            expectedReleaseDate: "03/17/2025",
            expectedRuntime: "5 weeks",
            context: {
                urls: [
                    "/software/confluence/templates",
                    "/software/confluence/templates/*"
                ]
            }
        }
    },
    jfind1249RemoveExpectationOfFreeOnTryCloudSignUp: {
        experimentName: "jfind-1249-remove-expectation-of-free-try-cloud-su",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variant",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variant" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variant"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Change the subheader copy on the Jira free try cloud signup page",
            expectedReleaseDate: "03/31/25",
            expectedRuntime: "14 days",
            context: {
                urls: ["/try/cloud/signup?bundle=jira-software&edition=free"]
            }
        }
    },
    jfind1315RemoveExpectationOfFreeConfluence: {
        experimentName: "jfind-1315-remove-expectation-of-free-confluence",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Change the subheader copy on the Confluence free try cloud signup page",
            expectedReleaseDate: "4/28/25",
            expectedRuntime: "76 days",
            context: {
                urls: ["/try/cloud/signup?bundle=confluence&edition=free"]
            }
        }
    },
    cpapiDeprecationJiraExperiment: {
        experimentName: "cpapi-wd-anchor-jira",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measures moving from CPAPI to Contentful data for (Jira) Anchor Pages.",
            expectedReleaseDate: "04/02/2025",
            expectedRuntime: "17 days",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    cpapiDeprecationConfExperiment: {
        experimentName: "cpapi-wd-anchor-conf",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measures moving from CPAPI to Contentful data for (Confluence) Anchor Pages.",
            expectedReleaseDate: "04/02/2025",
            expectedRuntime: "59 days",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    cpapiDeprecationJsmExperiment: {
        experimentName: "cpapi-wd-anchor-jsm",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measures moving from CPAPI to Contentful data for (Jira Service Management) Anchor Pages.",
            expectedReleaseDate: "04/02/2025",
            expectedRuntime: "28 days",
            context: {
                urls: ["/software/jira/service-management"]
            }
        }
    },
    cpapiDeprecationPricingJiraExperiment: {
        experimentName: "cpapi-wd-pricing-jira",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measures moving from CPAPI to Contentful data for Jira Pricing Page.",
            expectedReleaseDate: "04/15/2025",
            expectedRuntime: "42 days",
            context: {
                urls: ["/software/jira/pricing"]
            }
        }
    },
    wacSearchIndexV3Experiment: {
        experimentName: "wac-search-indexv3",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useEnglishLocale
        },
        metadata: {
            description:
                "New search index that includes page contents in addition to metadata",
            expectedReleaseDate: "04/04/2025",
            expectedRuntime: "18 days",
            clientOnly: true,
            context: {
                urls: ["*"]
            }
        }
    },
    jfindMatureExampleProjectExperiment: {
        experimentName: "jfind-mature-example-project",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Supports creating a new example project for Jira customers",
            expectedReleaseDate: "06/02/2025",
            expectedRuntime: "13 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    cfind3233TailorInProductJourneySubAnchors: {
        experimentName: "cfind-3233-in-product-sub-anchors-journeys",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Personalize the sub-Anchor journey with copy and visuals tailored to their ad intent",
            expectedReleaseDate: "04/14/2025",
            expectedRuntime: "9 weeks",
            context: {
                urls: []
            }
        }
    },
    gwp5258BackToProductV2: {
        experimentName: "gwp-5258-back-to-product-v2",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Return to product modal v2",
            expectedReleaseDate: "undefined",
            expectedRuntime: "undefined",
            clientOnly: true,
            context: {
                urls: ["*"]
            }
        }
    },
    gwp4948GartnerJiraProductTourExperiment: {
        experimentName: "gwp-4948-gartner-jira-product-tour-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "gartner section on jira product tour",
            expectedReleaseDate: "April 2025",
            expectedRuntime: "2 weeks",
            clientOnly: true,
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    gwp5619BackToProductV2JSM: {
        experimentName: "gwp-5619-back-to-product-v2-jsm",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Return to product modal v2 JSM",
            expectedReleaseDate: "undefined",
            expectedRuntime: "undefined",
            clientOnly: true,
            context: {
                urls: ["/software/jira/service-management/*"]
            }
        }
    },
    gwp3944SigninPrompt: {
        experimentName: "gwp-3944-signin-prompt-loggedout-d1to6-eligible",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Signin prompt for logged out users",
            expectedReleaseDate: "04/15/2025",
            expectedRuntime: "42 days",
            context: {
                urls: [
                    "/",
                    "/software",
                    "/software/pricing-calculator",
                    "/search",
                    "/try",
                    "/company",
                    "/agile",
                    "/agile/*",
                    "/customers",
                    "/customers/*",
                    "/devops",
                    "/devops/*",
                    "/git",
                    "/git/*",
                    "/licensing",
                    "/licensing/*",
                    "/migration",
                    "/migration/*",
                    "/platform",
                    "/platform/*",
                    "/project-management",
                    "/project-management/*",
                    "/team-playbook",
                    "/team-playbook/*",
                    "/work-management",
                    "/work-management/*"
                ]
            }
        }
    },
    gwp5831ConfluenceCrossflowHero: {
        experimentName: "gwp-5831-homepage-confluence-xflow",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedInHasJiraAndNotConfluence
        },
        metadata: {
            description: "Confluence Crossflow Hero",
            expectedReleaseDate: "05/01/2025",
            expectedRuntime: "7 weeks",
            clientOnly: true,
            context: {
                urls: ["/"]
            }
        }
    },
    cfind3443EmphasizeWorkEmail: {
        experimentName: "cfind-3443-emphasize-work-email-nbps-landing-wac",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description:
                "Nudge personal domain users to sign up with work email",
            expectedReleaseDate: "05/19/2025",
            expectedRuntime: "",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    cfind3467SignUpWithImageOverlay: {
        experimentName: "cfind-3467-signup-with-image-overlay",
        parameterName: "isTreatment",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test if giving users a consistent visual experience from signup to setup then we will increase d1to6",
            // TODO: update these values once determined
            expectedReleaseDate: "05/26/2025",
            expectedRuntime: "39 days",
            context: {
                urls: []
            }
        }
    }
} satisfies Manifest<unknown>;
